<template>
    <div class="min-h-screen text-foreground py-12 relative z-20">
        <div class="container mx-auto px-4">
            <h1 class="text-4xl font-bold text-white mb-8">Dashboard</h1>

            <!-- Appointments Section -->
            <div class="mt-12">
                <h2 class="text-2xl font-bold text-white mb-6">Upcoming Appointments</h2>
                <div v-if="upcomingAppointments.length > 0" class="space-y-4">
                    <div v-for="appointment in upcomingAppointments" :key="appointment._id"
                        class="bg-gray-800 p-4 rounded-lg">
                        <p class="text-white font-semibold">
                            <font-awesome-icon :icon="['fas', 'calendar']" class="mr-2 text-teal-400" />
                            {{ appointment.date }} — {{ appointment.time }}
                        </p>
                        <p class="text-gray-300 mt-1">{{ appointment.name }} <span class="text-gray-400">&lt;{{
                            appointment.email }}&gt;</span></p>
                        <p v-if="appointment.notes" class="text-sm text-gray-500 mt-1">{{ appointment.notes }}</p>
                        <div class="mt-2">
                            <button @click="cancelAppointment(appointment._id)"
                                class="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-300">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
                <p v-else class="text-gray-300">No upcoming appointments.</p>
            </div>

            <!-- Booking Links Section -->
            <div class="mt-12">
                <h2 class="text-2xl font-bold text-white mb-2">Booking Links</h2>
                <p class="text-gray-400 mb-4 break-all">
                    Public page: <span class="text-gray-300">{{ publicBookingUrl }}</span>
                    <button @click="copyLink(publicBookingUrl)"
                        class="ml-2 px-3 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-300">
                        {{ copiedLink === publicBookingUrl ? 'Copied!' : 'Copy' }}
                    </button>
                </p>
                <form @submit.prevent="createBookingLink" class="bg-gray-800 p-4 rounded-lg mb-6">
                    <p class="text-gray-300 mb-3">Create a one-time link for a client (their name and email will be
                        pre-filled).</p>
                    <div class="flex flex-col md:flex-row gap-2">
                        <input v-model="linkForm.client_name" type="text" placeholder="Client name (optional)"
                            class="flex-1 p-2 bg-gray-700 text-white rounded-lg">
                        <input v-model="linkForm.client_email" type="email" placeholder="Client email (optional)"
                            class="flex-1 p-2 bg-gray-700 text-white rounded-lg">
                        <button type="submit"
                            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-300">
                            Generate Link
                        </button>
                    </div>
                </form>
                <div v-if="bookingLinks.length > 0" class="space-y-4">
                    <div v-for="link in bookingLinks" :key="link._id" class="bg-gray-800 p-4 rounded-lg">
                        <p class="text-white font-semibold">
                            {{ link.client_name || 'Unnamed client' }}
                            <span v-if="link.client_email" class="text-gray-400 font-normal">&lt;{{ link.client_email
                                }}&gt;</span>
                            <span class="ml-2 text-xs px-2 py-1 rounded-full"
                                :class="link.used ? 'bg-gray-600 text-gray-300' : 'bg-green-600 text-white'">
                                {{ link.used ? 'Used' : 'Active' }}
                            </span>
                        </p>
                        <p class="text-sm text-gray-500 mt-1 break-all">{{ bookingUrl(link) }}</p>
                        <div class="mt-2 space-x-2">
                            <button v-if="!link.used" @click="copyLink(bookingUrl(link))"
                                class="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-300">
                                {{ copiedLink === bookingUrl(link) ? 'Copied!' : 'Copy Link' }}
                            </button>
                            <button @click="deleteBookingLink(link._id)"
                                class="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-300">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
                <p v-else class="text-gray-300">No booking links yet.</p>
            </div>

            <!-- Comments Awaiting Approval Section -->
            <div class="mt-12">
                <h2 class="text-2xl font-bold text-white mb-6">Comments Awaiting Approval</h2>
                <div v-if="pendingComments.length > 0" class="space-y-4">
                    <div v-for="comment in pendingComments" :key="comment._id" class="bg-gray-800 p-4 rounded-lg">
                        <p class="text-gray-300">{{ comment.content }} - <span class="text-gray-400">{{ comment.author
                                }}</span></p>
                        <p class="text-sm text-gray-500 mt-1">Project: {{ getProjectTitle(comment.project_id) }}</p>
                        <div class="mt-2 space-x-2">
                            <button @click="approveComment(comment._id)"
                                class="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-300">
                                Approve
                            </button>
                            <button @click="deleteComment(comment._id)"
                                class="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-300">
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
                <p v-else class="text-gray-300">No comments awaiting approval.</p>
            </div>

            <!-- Projects Management Section -->
            <div class="mt-12">
                <h2 class="text-2xl font-bold text-white mb-6">Manage Projects</h2>
                <button @click="openAddProjectModal"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-300 mb-6">
                    Add New Project
                </button>
                <div v-if="loading" class="text-center text-white mt-8">
                    <font-awesome-icon icon="spinner" spin class="text-2xl" />
                    <p class="mt-2">Loading projects...</p>
                </div>
                <draggable v-else-if="projects.length > 0" v-model="projects" @end="onDragEnd" item-key="_id"
                    class="space-y-4">
                    <template #item="{ element: project }">
                        <div class="bg-gray-800 p-4 rounded-lg">
                            <h3 class="text-xl font-bold text-white">{{ project.title }}</h3>
                            <p class="text-gray-300">{{ project.description }}</p>
                            <div class="mt-2 space-x-2">
                                <button @click="openEditProjectModal(project)"
                                    class="px-3 py-1 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors duration-300">
                                    Edit
                                </button>
                                <button @click="deleteProject(project._id)"
                                    class="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-300">
                                    Delete
                                </button>
                            </div>
                            <button @click="toggleComments(project)"
                                class="mt-2 px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-300">
                                {{ project.showComments ? 'Hide Comments' : 'Show Comments' }}
                            </button>
                            <div v-if="project.showComments" class="mt-4">
                                <h4 class="text-lg font-bold text-white mb-2">Comments</h4>
                                <div v-if="project.commentsLoading" class="text-center text-white">
                                    <font-awesome-icon icon="spinner" spin class="text-xl" />
                                    <p>Loading comments...</p>
                                </div>
                                <div v-else>
                                    <div v-if="project.comments.length > 0" class="space-y-2">
                                        <div v-for="comment in project.comments" :key="comment._id"
                                            class="bg-gray-700 p-2 rounded-lg">
                                            <p class="text-gray-300">{{ comment.content }}</p>
                                            <p class="text-sm text-gray-500 mt-1">— {{ comment.author }}</p>
                                            <button @click="deleteProjectComment(project._id, comment._id)"
                                                class="mt-2 px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-300">
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                    <div v-else class="text-gray-400">
                                        No comments yet.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </draggable>
                <p v-else class="text-gray-300">No projects found.</p>
            </div>

            <!-- Add/Edit Project Modal -->
            <div v-if="isProjectModalOpen"
                class="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center overflow-y-auto py-4">
                <div class="bg-gray-800 p-6 rounded-lg w-full max-w-md my-auto">
                    <h3 class="text-xl font-bold text-white mb-4">
                        {{ isEditingProject ? 'Edit Project' : 'Add New Project' }}
                    </h3>
                    <form @submit.prevent="isEditingProject ? updateProject() : addProject()">
                        <label class="block text-white mb-1">Title</label>

                        <input v-model="projectForm.title" type="text" placeholder="Title" required
                            class="w-full p-2 mb-4 bg-gray-700 text-white rounded-lg">
                        <label class="block text-white mb-1">Description (EN)</label>

                        <textarea v-model="projectForm.description" placeholder="Description"
                            class="w-full p-2 mb-4 bg-gray-700 text-white rounded-lg"></textarea>
                        <label class="block text-white mb-1">Description (FR)</label>
                        <textarea v-model="projectForm.description_fr" placeholder="Description en français"
                            class="w-full p-2 mb-4 bg-gray-700 text-white rounded-lg" rows="3" />
                        <label class="block text-white mb-1">GitHub URL</label>
                        <input v-model="projectForm.github_url" type="text" placeholder="GitHub URL"
                            class="w-full p-2 mb-4 bg-gray-700 text-white rounded-lg">
                        <label class="block text-white mb-1">Live URL</label>
                        <input v-model="projectForm.live_url" type="text" placeholder="Live URL"
                            class="w-full p-2 mb-4 bg-gray-700 text-white rounded-lg">
                        <!-- Image: upload a file or paste a link -->
                        <label class="block text-white mb-1">Image</label>
                        <div class="mb-4">
                            <div class="flex mb-2 bg-gray-700 rounded-lg p-1 text-sm">
                                <button type="button" @click="imageMode = 'upload'"
                                    class="flex-1 py-1 rounded-md transition-colors duration-300"
                                    :class="imageMode === 'upload' ? 'bg-blue-600 text-white' : 'text-gray-300'">
                                    Upload
                                </button>
                                <button type="button" @click="imageMode = 'link'"
                                    class="flex-1 py-1 rounded-md transition-colors duration-300"
                                    :class="imageMode === 'link' ? 'bg-blue-600 text-white' : 'text-gray-300'">
                                    Link
                                </button>
                            </div>

                            <label v-if="imageMode === 'upload'" @dragover.prevent @drop.prevent="onImageDrop"
                                class="flex flex-col items-center justify-center w-full p-4 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-blue-500 transition-colors duration-300">
                                <font-awesome-icon :icon="['fas', 'cloud-arrow-up']" class="text-2xl text-gray-400 mb-1" />
                                <span class="text-sm text-gray-300">Click or drop a picture here</span>
                                <span class="text-xs text-gray-500">JPG, PNG, WEBP or GIF — max 5 MB</span>
                                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" class="hidden"
                                    @change="onImageSelected">
                            </label>
                            <input v-else v-model="projectForm.image" type="text" placeholder="https://..."
                                @input="clearPendingImage"
                                class="w-full p-2 bg-gray-700 text-white rounded-lg">

                            <p v-if="imageError" class="text-sm text-red-400 mt-2">{{ imageError }}</p>

                            <div v-if="imagePreview" class="relative mt-2">
                                <img :src="imagePreview" alt="Preview" class="w-full h-40 object-cover rounded-lg">
                                <button type="button" @click="removeImage"
                                    class="absolute top-2 right-2 w-8 h-8 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors duration-300"
                                    aria-label="Remove image">
                                    <font-awesome-icon :icon="['fas', 'times']" />
                                </button>
                            </div>
                        </div>

                        <!-- Replace the tools input with our ToolSelector component -->
                        <div class="mb-4">
                            <label class="block text-white text-sm mb-1">Tools</label>
                            <ToolSelector v-model="projectForm.toolsArray" :all-tools="toolsList" />
                        </div>

                        <div class="flex justify-end space-x-2">
                            <button type="button" @click="closeProjectModal"
                                class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-300">
                                Cancel
                            </button>
                            <button type="submit" :disabled="savingProject"
                                class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-300"
                                :class="{ 'opacity-60': savingProject }">
                                <font-awesome-icon v-if="savingProject" icon="spinner" spin class="mr-1" />
                                {{ isEditingProject ? 'Update' : 'Add' }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useAuth0 } from "@auth0/auth0-vue";
import { useAuthStore } from "@/stores/auth";
import { computed, ref, onMounted } from "vue";
import { fetchAllComments, fetchComments, approveComment as approveCommentApi, deleteComment as deleteCommentApi } from "@/api/comments";
import projectsApi, { resolveImageUrl } from "@/api/projects"; // Import the default export from projects.js
import ToolSelector from "@/components/ToolSelector.vue"; // Import the new component
import draggable from "vuedraggable"; // Import vuedraggable
import {
    fetchAppointments as fetchAppointmentsApi,
    cancelAppointment as cancelAppointmentApi,
    fetchBookingLinks as fetchBookingLinksApi,
    createBookingLink as createBookingLinkApi,
    deleteBookingLink as deleteBookingLinkApi,
} from "@/api/appointments";

export default {
    components: {
        ToolSelector, // Register the component
        draggable // Register draggable
    },
    setup() {
        const { user, logout, getAccessTokenSilently } = useAuth0();
        const authStore = useAuthStore();
        const isAdminUser = computed(() => authStore.hasRole('Admin'));

        const pendingComments = ref([]);
        const projects = ref([]);
        const loading = ref(true);
        const isProjectModalOpen = ref(false);
        const isEditingProject = ref(false);
        const projectForm = ref({
            title: "",
            description: "",
            description_fr: "",
            github_url: "",
            live_url: "",
            image: "",
            tools: "", // Keep for backward compatibility
            toolsArray: [], // Add new array for the tool selector
        });
        const currentProjectId = ref(null);
        const savingProject = ref(false);

        // Project image: either a link, or a file uploaded when the project is saved
        const imageMode = ref("upload");
        const pendingImageFile = ref(null);
        const pendingImagePreview = ref(null);
        const imageError = ref("");
        const imagePreview = computed(() => pendingImagePreview.value || resolveImageUrl(projectForm.value.image));

        const clearPendingImage = () => {
            if (pendingImagePreview.value) URL.revokeObjectURL(pendingImagePreview.value);
            pendingImageFile.value = null;
            pendingImagePreview.value = null;
        };

        const setImageFile = (file) => {
            imageError.value = "";
            if (!file) return;
            if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
                imageError.value = "Only JPG, PNG, WEBP or GIF images are allowed.";
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                imageError.value = "Image must be 5 MB or less.";
                return;
            }
            clearPendingImage();
            pendingImageFile.value = file;
            pendingImagePreview.value = URL.createObjectURL(file);
        };

        const onImageSelected = (event) => {
            setImageFile(event.target.files[0]);
            event.target.value = "";
        };
        const onImageDrop = (event) => setImageFile(event.dataTransfer.files[0]);

        const removeImage = () => {
            clearPendingImage();
            projectForm.value.image = "";
        };

        const resetImageState = (image) => {
            clearPendingImage();
            imageError.value = "";
            imageMode.value = image && !image.startsWith("/pictures/") ? "link" : "upload";
        };

        // Upload the pending file (if any) and build the payload sent to the API
        const buildProjectData = async (token) => {
            const projectData = {
                ...projectForm.value,
                tools: projectForm.value.toolsArray
            };
            if (pendingImageFile.value) {
                projectData.image = await projectsApi.uploadImage(pendingImageFile.value, token);
            }
            return projectData;
        };

        const toolsList = ref([
            // Add your full list of tools here, or fetch from API if needed
            "Vue.js", "React", "Angular", "Node.js", "Express", "MongoDB", "TypeScript", "JavaScript", "Python", "Django", "Flask", "PostgreSQL", "MySQL", "TailwindCSS", "Bootstrap", "Sass", "Webpack", "Vite", "Jest", "Cypress"
            // ...etc
        ]);

        // Fetch pending comments
        const fetchPendingComments = async () => {
            try {
                const token = await getAccessTokenSilently();
                const allComments = await fetchAllComments(token);
                pendingComments.value = allComments.filter(comment => !comment.approved);
            } catch (error) {
                console.error("Error fetching comments:", error);
            }
        };

        // Approve a comment
        const approveComment = async (commentId) => {
            try {
                const token = await getAccessTokenSilently();
                await approveCommentApi(commentId, token);
                fetchPendingComments();
            } catch (error) {
                console.error("Error approving comment:", error);
            }
        };

        // Delete a comment
        const deleteComment = async (commentId) => {
            try {
                const token = await getAccessTokenSilently();
                await deleteCommentApi(commentId, token);
                fetchPendingComments();
            } catch (error) {
                console.error("Error deleting comment:", error);
            }
        };

        // Fetch projects
        const fetchProjects = async () => {
            try {
                const token = await getAccessTokenSilently();
                const data = await projectsApi.getProjects(token);
                projects.value = data.map(project => ({
                    ...project,
                    comments: [],
                    commentsLoading: false,
                    showComments: false,
                }));
            } catch (error) {
                console.error("Error fetching projects:", error);
            } finally {
                loading.value = false;
            }
        };

        // Get project title by ID
        const getProjectTitle = (projectId) => {
            const project = projects.value.find(project => project._id === projectId);
            return project ? project.title : 'Unknown Project';
        };

        // Open add project modal
        const openAddProjectModal = () => {
            isEditingProject.value = false;
            projectForm.value = {
                title: "",
                description: "",
                description_fr: "",
                github_url: "",
                live_url: "",
                image: "",
                tools: "",
                toolsArray: [],
            };
            resetImageState("");
            isProjectModalOpen.value = true;
        };

        // Open edit project modal
        const openEditProjectModal = (project) => {
            isEditingProject.value = true;
            currentProjectId.value = project._id;
            projectForm.value = {
                ...project,
                description_fr: project.description_fr || "",
                tools: project.tools.join(", "),
                toolsArray: [...project.tools] // Add the tools array for the selector
            };
            resetImageState(project.image);
            isProjectModalOpen.value = true;
        };

        // Close project modal
        const closeProjectModal = () => {
            clearPendingImage();
            isProjectModalOpen.value = false;
        };

        // Add a new project
        const addProject = async () => {
            savingProject.value = true;
            try {
                const token = await getAccessTokenSilently();
                const projectData = await buildProjectData(token);
                await projectsApi.addProject(projectData, token);
                fetchProjects();
                closeProjectModal();
            } catch (error) {
                console.error("Error adding project:", error);
                imageError.value = error.response?.data?.detail?.toString() || "Could not save the project.";
            } finally {
                savingProject.value = false;
            }
        };

        // Update a project
        const updateProject = async () => {
            savingProject.value = true;
            try {
                const token = await getAccessTokenSilently();
                const projectData = await buildProjectData(token);
                await projectsApi.updateProject(currentProjectId.value, projectData, token);
                fetchProjects();
                closeProjectModal();
            } catch (error) {
                console.error("Error updating project:", error);
                imageError.value = error.response?.data?.detail?.toString() || "Could not save the project.";
            } finally {
                savingProject.value = false;
            }
        };

        // Delete a project
        const deleteProject = async (projectId) => {
            try {
                const token = await getAccessTokenSilently();
                await projectsApi.deleteProject(projectId, token);
                fetchProjects();
            } catch (error) {
                console.error("Error deleting project:", error);
            }
        };

        // Load comments for a project
        const loadComments = async (project) => {
            project.commentsLoading = true;
            try {
                const token = await getAccessTokenSilently();
                const comments = await fetchComments(project._id, token);
                project.comments = comments;
            } catch (error) {
                console.error('Error loading comments:', error);
            } finally {
                project.commentsLoading = false;
            }
        };

        // Delete a comment from a project
        const deleteProjectComment = async (projectId, commentId) => {
            try {
                const token = await getAccessTokenSilently();
                await deleteCommentApi(commentId, token);
                const project = projects.value.find(project => project._id === projectId);
                if (project) {
                    project.comments = project.comments.filter(comment => comment._id !== commentId);
                }
            } catch (error) {
                console.error('Error deleting comment:', error);
            }
        };

        // Toggle comments visibility
        const toggleComments = (project) => {
            project.showComments = !project.showComments;
            if (project.showComments && project.comments.length === 0) {
                loadComments(project);
            }
        };

        // Appointments
        const appointments = ref([]);
        const upcomingAppointments = computed(() => {
            const now = new Date();
            return appointments.value.filter(a => new Date(`${a.date}T${a.time}`) >= now);
        });

        const fetchAppointments = async () => {
            try {
                const token = await getAccessTokenSilently();
                appointments.value = await fetchAppointmentsApi(token);
            } catch (error) {
                console.error("Error fetching appointments:", error);
            }
        };

        const cancelAppointment = async (appointmentId) => {
            if (!confirm("Cancel this appointment? The slot will become available again.")) return;
            try {
                const token = await getAccessTokenSilently();
                await cancelAppointmentApi(appointmentId, token);
                fetchAppointments();
            } catch (error) {
                console.error("Error cancelling appointment:", error);
            }
        };

        // Booking links
        const bookingLinks = ref([]);
        const linkForm = ref({ client_name: "", client_email: "" });
        const copiedLink = ref(null);
        const publicBookingUrl = `${window.location.origin}/appointment`;
        const bookingUrl = (link) => `${publicBookingUrl}/${link.token}`;

        const copyLink = async (url) => {
            try {
                await navigator.clipboard.writeText(url);
                copiedLink.value = url;
                setTimeout(() => { if (copiedLink.value === url) copiedLink.value = null; }, 2000);
            } catch (error) {
                console.error("Error copying link:", error);
            }
        };

        const fetchBookingLinks = async () => {
            try {
                const token = await getAccessTokenSilently();
                bookingLinks.value = await fetchBookingLinksApi(token);
            } catch (error) {
                console.error("Error fetching booking links:", error);
            }
        };

        const createBookingLink = async () => {
            try {
                const token = await getAccessTokenSilently();
                const link = await createBookingLinkApi(linkForm.value, token);
                linkForm.value = { client_name: "", client_email: "" };
                await fetchBookingLinks();
                copyLink(bookingUrl(link));
            } catch (error) {
                console.error("Error creating booking link:", error);
            }
        };

        const deleteBookingLink = async (linkId) => {
            try {
                const token = await getAccessTokenSilently();
                await deleteBookingLinkApi(linkId, token);
                fetchBookingLinks();
            } catch (error) {
                console.error("Error deleting booking link:", error);
            }
        };

        // Reorder projects after drag
        const onDragEnd = async () => {
            try {
                const order = projects.value.map(p => p._id);
                const token = await getAccessTokenSilently();
                await projectsApi.reorderProjects(order, token);
                // Optionally refetch or trust the local order
            } catch (error) {
                console.error("Error reordering projects:", error);
            }
        };

        onMounted(() => {
            if (isAdminUser.value) {
                fetchPendingComments();
                fetchProjects();
                fetchAppointments();
                fetchBookingLinks();
            }
        });

        return {
            user,
            logout,
            isAdminUser,
            pendingComments,
            projects,
            loading,
            isProjectModalOpen,
            isEditingProject,
            projectForm,
            approveComment,
            deleteComment,
            openAddProjectModal,
            openEditProjectModal,
            closeProjectModal,
            addProject,
            updateProject,
            deleteProject,
            toggleComments,
            getProjectTitle,
            deleteProjectComment,
            onDragEnd,
            toolsList,
            savingProject,
            imageMode,
            imageError,
            imagePreview,
            clearPendingImage,
            onImageSelected,
            onImageDrop,
            removeImage,
            upcomingAppointments,
            cancelAppointment,
            bookingLinks,
            linkForm,
            copiedLink,
            publicBookingUrl,
            bookingUrl,
            createBookingLink,
            deleteBookingLink,
            copyLink,
        };
    }
};
</script>